import React from "react";
import { fmt$Denominated } from "../../utils/format";

/**
 * The fund-specific half of the campaign review screen.
 *
 * WHY IT EXISTS. CampaignViewPage's type branch was a BINARY
 * `type === "EQUITY" ? equity : lending`, which was correct for exactly as long
 * as there were two product types. A FUND fell into the else and was shown six
 * loan fields that are all NULL for it, under labels like "desired loan's
 * duration in months" — while security_type, which a fund DOES set (Units /
 * Partnership Units / Trust Units), rendered only inside the equity branch and
 * was therefore invisible on the one screen where a fund gets approved.
 *
 * Nothing errored. It silently rendered the wrong thing, which is why no test
 * and no log ever mentioned it.
 *
 * EXTRACTED rather than left inline so it can be tested at all: the page it came
 * from needs routing, redux and a populated API response to mount.
 *
 * ABSENCE IS RENDERED, NOT HIDDEN. Every row shows "— not set" rather than
 * disappearing, because this is a REVIEW screen: an approver needs to see that a
 * fund declares no jurisdiction, and a row that vanishes cannot be noticed. That
 * is the opposite of the investor-facing detail page, which omits empty rows
 * because an investor has no decision to make about a blank.
 */

const UNSET = "— not set";

const text = (v) => (v === null || v === undefined || v === "" ? UNSET : v);

// Ordered as the offering reads: what it is, what it costs to enter, what it is
// raising, then how it is structured and run.
export const FUND_TEXT_ROWS = [
  ["Type of Fund", "fund_category"],
  ["Type of Security", "security_type"],
  ["Risk Level", "risk_profile"],
  ["Lock-Up Period", "lockup_period"],
  ["Target Return", "target_arr"],
  ["Investment Horizon", "investment_horizon"],
  ["Legal Structure", "legal_structure"],
  ["Jurisdiction", "jurisdiction"],
  ["Base Currency", "base_currency"],
  ["Fund Manager", "fund_manager_name"],
  ["Management Fee", "management_fee"],
  ["Performance Fee", "performance_fee"],
  ["Subscriptions", "subscription_frequency"],
  ["Valuations", "valuation_frequency"],
  ["Benchmark", "benchmark"],
];

export const FUND_MONEY_ROWS = [
  ["Target Raise", "target_raise"],
  ["Maximum Offering", "maximum_offering"],
];

const Row = ({ label, children }) => (
  <div className="detailItem">
    <span className="itemKey">{label}</span>
    <span className="itemValue">{children}</span>
  </div>
);

export default function FundDetailFields({ userInfo, currency }) {
  const u = userInfo || {};

  return (
    <>
      {FUND_TEXT_ROWS.slice(0, 4).map(([label, key]) => (
        <Row key={key} label={label}>{text(u[key])}</Row>
      ))}

      {/* fmt$Denominated, never fmt$: a fund that has not stated a target raise must
          not be reported as raising $0.00 on the screen where it is approved. */}
      {FUND_MONEY_ROWS.map(([label, key]) => (
        <Row key={key} label={label}>{fmt$Denominated(u[key], 2, currency)}</Row>
      ))}

      {FUND_TEXT_ROWS.slice(4).map(([label, key]) => (
        <Row key={key} label={label}>{text(u[key])}</Row>
      ))}

      <Row label="Redemption Notice">
        {/* Guarded on null, not on falsiness: 0 days is a real notice period and
            must not read as unset. */}
        {u.redemption_notice_days === null || u.redemption_notice_days === undefined
          ? UNSET
          : `${u.redemption_notice_days} days`}
      </Row>

      <Row label="Inception Date">
        {/* The DATEONLY column arrives as a bare YYYY-MM-DD and is shown
            verbatim. Passing it through a Date renders the PREVIOUS day west of
            UTC, and because every real inception date is the 1st of a month that
            is the previous MONTH — the defect that had two live production funds
            publicly misstating a term of their offering. */}
        {String(u.inception_date || "").slice(0, 10) || UNSET}
      </Row>

      {/* The one row that is omitted when empty rather than shown as unset: it
          is free prose an issuer may legitimately not have written, not a term
          whose absence an approver needs to weigh. */}
      {u.important_information ? (
        <Row label="Important Information">{u.important_information}</Row>
      ) : null}
    </>
  );
}
